import * as React from 'react';
import { mergeStyles } from '@cian/components';
import { Popup } from '../../ui/popup';
import { VideoPlayer } from '../video-player';

import * as styles from './styles.css';

const NARROW_ROW_ITEMS_COUNT = 2;
const ITEM_WIDTH_OFFSET = 317;

export interface IVideoGalleryItem {
  videoPreviewUrl: string;
  videoUrl: string;
  title: string;
  text?: string;
}

interface IVideoGalleryProps {
  items: IVideoGalleryItem[];
}

interface IVideoGalleryState {
  rowItemsCount: number;
  offsetIndex: number;
  videoPopupIndex: number | null;
}

export class VideoGallery extends React.Component<IVideoGalleryProps, IVideoGalleryState> {
  public state = {
    rowItemsCount: NARROW_ROW_ITEMS_COUNT,
    offsetIndex: 0,
    videoPopupIndex: null,
  };

  public componentDidMount() {
    const mq = window.matchMedia('(min-width: 1312px)');

    this.mqEventHandler(mq);
    mq.addListener(this.mqEventHandler);
  }

  public componentDidUpdate(prevProps: IVideoGalleryProps) {
    if (prevProps.items !== this.props.items) {
      this.setState({ offsetIndex: 0 });
    }
  }

  public render() {
    const { items } = this.props;
    const { rowItemsCount, offsetIndex } = this.state;
    const itemsCount = items.length;

    return (
      <div className={styles['container']}>
        <div className={styles['video-navigation']}>
          {itemsCount > rowItemsCount && (
            <>
              <button disabled={offsetIndex < 1} onClick={() => this.changeOffset(false)} />
              <button disabled={offsetIndex >= itemsCount - rowItemsCount} onClick={() => this.changeOffset(true)} />
            </>
          )}
        </div>

        <div key={items[0].title} {...mergeStyles(styles['wrapper'], this.calcGalleryOffset())}>
          {items.map((item, index) => (
            <div key={item.title} className={styles['video-container']} onClick={() => this.setPopupIndex(index)}>
              <div
                {...mergeStyles(
                  styles['video-preview'],
                  item.videoPreviewUrl && { backgroundImage: `url(${item.videoPreviewUrl})` },
                )}
              />
              <div className={styles['video-title']} dangerouslySetInnerHTML={{ __html: item.title }} />
            </div>
          ))}
        </div>

        {this.renderPopup()}
      </div>
    );
  }

  public mqEventHandler = (event: MediaQueryList | MediaQueryListEvent) => {
    const rowItemsCount = event.matches ? NARROW_ROW_ITEMS_COUNT + 1 : NARROW_ROW_ITEMS_COUNT;

    this.setState((state, props) => {
      const { offsetIndex } = state;
      let newOffsetIndex = offsetIndex;

      if (event.matches && offsetIndex > 0 && offsetIndex + rowItemsCount > props.items.length) {
        newOffsetIndex--;
      }

      return {
        rowItemsCount,
        offsetIndex: newOffsetIndex,
      };
    });
  };

  private changeOffset = (forwardDirection: boolean) =>
    this.setState((state, props) => {
      const count = props.items.length;
      const { rowItemsCount, offsetIndex } = state;
      let newOffsetIndex = offsetIndex;

      if (forwardDirection && offsetIndex < count - rowItemsCount) {
        newOffsetIndex = offsetIndex + 1;
      }

      if (!forwardDirection && offsetIndex > 0) {
        newOffsetIndex = offsetIndex - 1;
      }

      return { offsetIndex: newOffsetIndex };
    });

  private calcGalleryOffset() {
    const { offsetIndex } = this.state;

    return { transform: `translateX(-${offsetIndex * ITEM_WIDTH_OFFSET}px)` };
  }

  private setPopupIndex = (index: number | null) => this.setState({ videoPopupIndex: index });

  private renderPopup() {
    const { videoPopupIndex } = this.state;

    if (videoPopupIndex === null) {
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { videoUrl, title, text } = this.props.items[videoPopupIndex!];

    return (
      <Popup clickOutside={() => this.setPopupIndex(null)}>
        <div className={styles['video-popup-container']}>
          <VideoPlayer
            containerStyle={styles['video-popup-player']}
            videoURL={videoUrl}
            videoSettings={`autoplay=1&mute=1&fs=1&loop=1&playlist=${videoUrl}`}
          />

          <div className={styles['video-popup-description']}>
            <h3 className={styles['video-popup-title']} dangerouslySetInnerHTML={{ __html: title }} />
            <div className={styles['video-popup-text']}>{text}</div>
          </div>
        </div>
      </Popup>
    );
  }
}
